import React, { useState } from 'react';
import styles from './styles.module.scss';
import Flex from 'src/components/Flex';
import Card from 'src/components/Card';
import Button from 'src/components/Button';
import Input from 'src/components/Input';
import Icon from 'src/components/Icon';
import { IntegrationConfig } from 'src/api/surveyIntegration';
import { useUpdateSurveyIntegrationApiKey } from 'src/queries/useSurveyIntegration';

interface ApiKeySectionProps {
    config: IntegrationConfig;
}

const HELPER_TEXT = 'Generate a new API key in the survey dashboard at Settings > API Keys, then paste it here.';

function errorMessage(error: unknown): string {
    return error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.';
}

function ApiKeySection({ config }: ApiKeySectionProps) {
    const [apiKey, setApiKey] = useState('');
    const updateApiKey = useUpdateSurveyIntegrationApiKey();

    const handleUpdate = async () => {
        try {
            await updateApiKey.mutateAsync(apiKey.trim());
            setApiKey('');
        } catch {
            // Error surfaced via updateApiKey.isError in renderResult(); keep the entered key.
        }
    };

    const renderResult = () => {
        if (updateApiKey.isError) {
            return (
                <Flex direction="row" align="center" className={styles.statusRow}>
                    <Icon type="fa" name="times-circle" className={styles.statusFailed} />
                    <span className={styles.statusFailed}>{errorMessage(updateApiKey.error)}</span>
                </Flex>
            );
        }
        if (updateApiKey.isSuccess) {
            if (updateApiKey.data.validated) {
                return (
                    <Flex direction="row" align="center" className={styles.statusRow}>
                        <Icon type="fa" name="check-circle" className={styles.statusVerified} />
                        <span className={styles.statusVerified}>API key updated and verified.</span>
                    </Flex>
                );
            }
            return (
                <Flex direction="row" align="center" className={styles.statusRow}>
                    <Icon type="fa" name="exclamation-triangle" className={styles.statusWarning} />
                    <span className={styles.statusWarning}>
                        API key saved, but the connection could not be verified. Check the key and test the
                        connection.
                    </span>
                </Flex>
            );
        }
        return null;
    };

    return (
        <Card className={styles.cardWrap} label="API Key">
            <Flex direction="column" className={styles.sectionBody}>
                <Input label="Current API Key" value={config.maskedApiKey ?? 'No API key set'} disabled />

                <Input
                    label="New API Key"
                    type="password"
                    value={apiKey}
                    disabled={updateApiKey.isLoading}
                    placeholder="Paste the new API key"
                    onChangeText={(value: string) => setApiKey(value)}
                />

                <div className={styles.helperText}>{HELPER_TEXT}</div>

                <Flex direction="row" align="center" className={styles.buttonRow}>
                    <Button
                        type="primary"
                        text="Update API Key"
                        isLoading={updateApiKey.isLoading}
                        disabled={!apiKey.trim()}
                        onClick={handleUpdate}
                    />
                </Flex>

                {renderResult()}
            </Flex>
        </Card>
    );
}

export default ApiKeySection;
